import { Loader, ErrorMessage, SharePreviewCard } from '@communityshare/ui-shared';
import { useConnect } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQueryMutation } from '@lore/query-connect';
import useEventImage from '@/hooks/useEventImage';
import useEventChannel from '@/hooks/useEventChannel';
import usePreviewImages from '@/features/share-common/hooks/usePreviewImages';
import fallbackImage from '@assets/boy_magnifying_glass_butterfly.jpg';

// Window name for the preview tab: repeat clicks reuse the tab instead of
// opening new ones, and gives the BroadcastChannel peer a known identity.
const PREVIEW_WINDOW_NAME = 'cs-share-event-preview';

EventLinkPreview.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
    data: PropTypes.shape({
      activityId: PropTypes.number,
      siteId: PropTypes.number,
      title: PropTypes.string.isRequired,
      shared: PropTypes.bool
    }).isRequired
  }).isRequired
};

export default function EventLinkPreview(props) {
  const { event } = props;
  const { t } = useTranslation();
  // Default to tag 1 so byId never throws for events without an activity
  const activity = useConnect('tag.byId', {
    id: event?.data?.activityId ?? 1
  });
  const siteId = event?.data?.siteId ?? null;
  // Pass 0 when siteId is absent so the blueprint doesn't throw 'Missing id';
  // a 0-id query returns NOT_FOUND harmlessly.
  const site = useConnect('site.byId', {
    id: siteId ?? 0
  });
  const [hostname, setHostname] = useState('...');
  // Local selection; null defers to the saved image (or the default)
  const [selectedImageId, setSelectedImageId] = useState(null);
  const eventImage = useEventImage(event.id);
  const { loading: imagesLoading, images } = usePreviewImages(event.data.activityId);
  const broadcast = useEventChannel(event.id);
  const defaultSavedRef = useRef(false);
  const markSharedRef = useRef(false);
  const sharedMutation = useQueryMutation('event.update', event);

  // Opening the share dialog is currently the only signal that an event
  // has been shared, so flip the flag once if it isn't already set.
  useEffect(() => {
    const isReady = [PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(event.state) && event.data;
    const alreadyShared = event.data?.shared === true || event.data?.shared === 1 || event.data?.shared === '1';
    if (isReady && !alreadyShared && !markSharedRef.current) {
      markSharedRef.current = true;
      sharedMutation.mutateAsync({ shared: true }).catch((error) => {
        // Allow a retry on the next render if the save failed.
        markSharedRef.current = false;
        console.error('Failed to mark event as shared', error);
      });
    }
  }, [event.state, event.data]);

  useEffect(() => {
    setHostname(window.location.hostname);
  }, []);

  // An event with no saved image gets the default persisted once,
  // so the share page renders the same photo the preview shows.
  useEffect(() => {
    if (eventImage.isSuccess && !eventImage.image && !imagesLoading && !defaultSavedRef.current) {
      defaultSavedRef.current = true;
      const image = images[0];
      eventImage.upsert({ imageUrl: image.src, width: image.width, height: image.height });
    }
  }, [eventImage.isSuccess, eventImage.image, imagesLoading]);

  if ([PayloadStates.FETCHING, PayloadStates.INITIAL_STATE].includes(event.state) || eventImage.isLoading || imagesLoading) {
    return (
      <div className="flex flex-col justify-center h-[80vh]">
        <Loader/>
      </div>
    )
  }

  if (![PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(event.state) || !event.data) {
    return (
      <div className="flex flex-col items-center">
        <ErrorMessage v2={true}>
          {t('events.share.data_error')}
        </ErrorMessage>
      </div>
    )
  }

  // Local selection wins (optimistic), then the saved image;
  // null renders the empty state until an image exists
  const savedImage = images.find(image => image.src === eventImage.image?.imageUrl);
  const shareImage = images.find(image => image.id === selectedImageId) ?? savedImage ?? null;


  // The preview swaps immediately while the save runs;
  // a failure surfaces through eventImage.upsertError below.
  function onShareImageChange(imageId) {
    setSelectedImageId(imageId);
    const image = images.find(image => image.id === imageId);
    eventImage.upsert({
      imageUrl: image.src,
      width: image.width,
      height: image.height
    });
    broadcast({ imageUrl: image.src });
  }

  function onPreview() {
    window.open(
      `${window.location.origin}/share/event/${event.id}`,
      `${PREVIEW_WINDOW_NAME}-${event.id}`
    );
  }

  const siteLabel = siteId && site.state !== PayloadStates.FETCHING ?
    ` | ${site?.data?.city ?? ''}, ${site?.data?.state ?? ''}` :
    '';

  return (
    <SharePreviewCard
      title={t('events.share.link_preview')}
      imageSrc={shareImage?.src}
      imageKey={shareImage?.id}
      imageAlt={t('events.share.preview_image_alt')}
      // Curated WordPress URLs can 404; fall back to a bundled image.
      imageFallback={<img src={fallbackImage} className="w-full h-full object-cover" alt="" />}
      urlText={`${hostname}/share/event/${event.id}`}
      eyebrowText={(
        <>
          {activity.state === PayloadStates.FETCHING && <Loader/>}
          {activity?.data?.name ?? '...'}{siteLabel}
        </>
      )}
      primaryText={t('share.common.cta')}
      secondaryText={<>&ldquo;{event.data.title}&rdquo;</>}
      previewLabel={t('events.share.preview_page')}
      onPreview={onPreview}
      images={images}
      selectedImageId={shareImage?.id ?? null}
      onImageChange={onShareImageChange}
      swapPhotoLabel={t('events.share.swap_photo')}
      getImageLabel={(image, index) => t('events.share.photo_option', { number: index + 1 })}
      errorMessage={eventImage.upsertError ? t('events.share.image_error') : null}
    />
  );
}
